import { StyleSheet } from 'react-native';

import { useTheme } from './theme';

// Styles for ExerciseSwapPicker, split out of the screen (SP-SWAPPICKER):
// 139 of its 490 lines — over a quarter of the file — were this one
// function, called exactly once. Pure theme -> StyleSheet mapping, no
// state and no closure over the screen.

export function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    safe: { flex: 1 },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
    },
    cancel: {
      color: p.brand500,
      fontSize: t.sizes.base,
    },
    // See the add picker's twin: a bare label is a ~20pt touch box.
    cancelHit: { minHeight: 44, justifyContent: 'center' },
    // `flex: 1` + centred text: the right cell is a fixed spacer again since
    // DS-42 moved the place chip out of the header, so the title has the whole
    // middle and truncates rather than wrapping.
    title: {
      flex: 1,
      textAlign: 'center',
      marginHorizontal: s.s3,
      color: p.ink,
      fontSize: t.sizes.base,
      fontWeight: '600',
    },
    // Balances the cancel affordance on the left; the 56-vs-64 divergence with
    // the add picker is the fake-centering the planned <ScreenHeader> wave ends.
    headerSpacer: { width: 56 },
    currentLabel: {
      paddingHorizontal: s.s5,
      paddingBottom: s.s2,
      color: p.ink3,
      fontSize: t.sizes.sm,
    },
    // DS-42 — the band headings. Padding only: the type comes from
    // <SectionHeader>, so the three bands cannot drift apart the way the three
    // "text + ▾" controls did.
    bandHeader: {
      paddingHorizontal: s.s5,
      paddingTop: s.s4,
      paddingBottom: s.s2,
    },
    // DS-42 — the line that states what the gesture above does to data already
    // logged. Same token recipe as <ChoiceTray>'s `footnote` (ink3 / sizes.sm),
    // deliberately: it is the same idiom, "a consequence worth stating before
    // the tap". ⛔ NOT an <InfoPill> — that hides its text behind a toggle, and
    // a consequence you have to ask for is one nobody reads.
    consequence: {
      paddingHorizontal: s.s5,
      paddingBottom: s.s3,
      color: p.ink3,
      fontSize: t.sizes.sm,
    },
    // ⛔ `flexShrink` belongs HERE, not on `currentLabel`. On the label it
    // applied in both branches, and in the screen's flex COLUMN that made it
    // the only shrinkable child: it absorbed the whole overflow and the line
    // rendered clipped under the search field (Antenore, dev 4.15.2). Inside
    // the row it does the job it was meant for, letting a long exercise name
    // yield to the badge instead of pushing it off-screen.
    currentLabelInRow: { flexShrink: 1 },
    // Always rendered now, machines known or not: the tray is the only place
    // an instance can be created, so gating this line on "two or more exist"
    // meant the first one never could be.
    currentLine: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingRight: s.s5,
    },
    currentName: {
      color: p.ink,
      fontWeight: '600',
    },
    // EQ-1-GYM-COLDSTART — breathing room between the gear chip and the ✕.
    // A margin on the button rather than a `gap` on `currentLine`: that row
    // has fragile-flex history (see `currentLabelInRow` above), and a gap
    // would also re-space the label/chip pair it was tuned without.
    currentUnavailable: { marginLeft: s.s3 },
    // The sunk candidate: the row as it always was, plus what it now means and
    // the way back. ⛔ Sunk, never filtered — nothing disappears (D4's rule).
    unavailableRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingRight: s.s5,
    },
    unavailableRowMain: { flex: 1 },
    // ⛔ Never colour-only: a glyph-free written badge, in ink2 on surface2
    // with the same borderStrong recipe `machineBadge` uses, so it reads as a
    // shape and as words in both theme families.
    unavailableBadge: {
      alignSelf: 'flex-start',
      marginLeft: s.s5,
      marginBottom: s.s4,
      paddingHorizontal: s.s2,
      paddingVertical: 2,
      backgroundColor: p.surface2,
      borderColor: p.borderStrong,
      borderWidth: 1,
      borderRadius: r.r2,
    },
    // ⛔ `sm`, deliberately one step above the `xs` of machineBadge and
    // reasonChip in this same file (Antenore 2026-08-17, on the a11y audit).
    // Those two are chrome you can skip; this one is the ONLY non-colour proof
    // that a row was declared missing, so it carries the state rather than
    // decorating it. Matching its neighbours' size would have been consistent
    // and wrong.
    unavailableBadgeText: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: '700',
    },
    unavailableRestore: { marginLeft: s.s3 },
    // Mirrors the add picker's own searchWrap (pickerStyles.ts): search and
    // the filters control share one line.
    searchWrap: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s3,
      paddingHorizontal: s.s5,
      paddingBottom: s.s3,
    },
    search: {
      flex: 1,
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      color: p.ink,
      fontSize: t.sizes.base,
      paddingHorizontal: s.s4,
      paddingVertical: s.s3,
    },
    clearFilters: {
      paddingHorizontal: s.s5,
      paddingVertical: s.s2,
      alignSelf: 'flex-end',
    },
    clearFiltersPressed: { opacity: 0.7 },
    clearFiltersText: {
      color: p.brand500,
      fontSize: t.sizes.sm,
      fontWeight: '600',
    },
    sectionHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: p.surface2,
      paddingHorizontal: s.s5,
      paddingVertical: s.s3,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderColor: p.border,
    },
    sectionTitle: {
      color: p.ink2,
      fontSize: t.sizes.xs,
      fontWeight: '700',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
    },
    sectionCount: {
      color: p.ink3,
      fontSize: t.sizes.sm,
      fontVariant: ['tabular-nums'],
    },
    row: {
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
      gap: s.s2,
    },
    rowPressed: {
      backgroundColor: p.surface2,
    },
    rowNameLine: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    rowName: {
      color: p.ink,
      fontSize: t.sizes.base,
      fontWeight: '500',
      flexShrink: 1,
    },
    rowSub: {
      color: p.ink3,
      fontSize: t.sizes.sm,
    },
    chipsRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: s.s2,
      marginTop: s.s1,
    },
    reasonChip: {
      paddingHorizontal: s.s2,
      paddingVertical: 2,
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: 999,
    },
    reasonChipText: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: '600',
    },
    // EQ-1 — the machine badge on a row that will expand, and the tray it
    // expands into. Box radius (r2), never the 999 pill the design gate bans:
    // `reasonChip` above predates the gate and is tracked, not copied.
    machineBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s1,
      marginLeft: 'auto',
      paddingHorizontal: s.s2,
      paddingVertical: 2,
      backgroundColor: p.surface2,
      // `borderStrong`, not `border`: a `border` hairline on a `surface2` fill
      // computes at ~1.1:1, i.e. the box does not read as a shape at all —
      // the same defect DS-9/DS-23 already fixed on IconButton's identical box.
      borderColor: p.borderStrong,
      borderWidth: 1,
      borderRadius: r.r2,
    },
    machineBadgeText: {
      color: p.ink2,
      fontSize: t.sizes.xs,
      fontWeight: '700',
      fontVariant: ['tabular-nums'],
    },
    // The machine-tray styles that used to live here moved into <ChoiceTray>
    // (@heraion/ui) on 2026-08-13, comments and all. They were the reason
    // the tray could not leave this screen.
    separator: {
      height: StyleSheet.hairlineWidth,
      marginLeft: s.s5,
    },
    center: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      padding: s.s5,
      gap: s.s3,
    },
    emptyText: {
      color: p.ink3,
      fontSize: t.sizes.sm,
      textAlign: 'center',
    },
  });
}
